"use client";

import { faAngleLeft, faAngleRight } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { cn } from "@tosize/ui";
import type { ComponentProps, FC, RefObject } from "react";
import SwiperCore from "swiper";
import "swiper/css";
import "swiper/css/a11y";
import "swiper/css/keyboard";
import "swiper/css/mousewheel";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { A11y, Keyboard, Mousewheel, Navigation, Pagination } from "swiper/modules";
import { Swiper, type SwiperProps } from "swiper/react";
import useSwiperRef from "~/hooks/useSwiperRef";
import styles from "./SwiperSlider.module.css";

SwiperCore.use([A11y, Keyboard, Mousewheel, Navigation, Pagination]);

interface SwiperSliderProps extends SwiperProps {
	navigationEnabled?: boolean;
	navigationSize?: NavigationProps["size"];
	paginationEnabled?: boolean;
	keyboardEnabled?: boolean;
	paginationClassName?: string;
}

export const SwiperSlider: FC<SwiperSliderProps> = ({
	navigationEnabled = true,
	paginationEnabled = true,
	keyboardEnabled = true,
	paginationClassName,
	navigationSize,
	children,
	className,
	id,
	...props
}) => {
	const paginationElRef = useSwiperRef();
	const nextElRef = useSwiperRef();
	const prevElRef = useSwiperRef();

	return (
		<div className={cn("relative", className, styles.root)} id={id}>
			{navigationEnabled && (
				<SliderNavigation prevElRef={prevElRef} nextElRef={nextElRef} size={navigationSize} />
			)}
			<Swiper
				pagination={
					paginationEnabled && {
						el: paginationElRef?.current,
						clickable: true,
						dynamicBullets: true,
					}
				}
				keyboard={keyboardEnabled}
				navigation={navigationEnabled && { prevEl: prevElRef?.current, nextEl: nextElRef?.current }}
				{...props}
			>
				{children}
			</Swiper>
			{paginationEnabled && (
				<SliderPagination className={paginationClassName} paginationElRef={paginationElRef} />
			)}
		</div>
	);
};

interface NavigationProps {
	prevElRef: RefObject<HTMLButtonElement | null>;
	nextElRef: RefObject<HTMLButtonElement | null>;
	size?: "DEFAULT" | "small";
}

const SliderNavigation: FC<NavigationProps> = ({ prevElRef, nextElRef, size = "DEFAULT" }) => {
	return (
		<>
			<button
				type="button"
				title="previous slide"
				ref={prevElRef}
				className={cn(
					{
						"h-10 w-10 text-xl": size === "DEFAULT",
						"h-6 w-6": size === "small",
					},
					"-translate-y-1/2 invisible absolute top-1/2 left-4 z-9 rounded-full bg-primary text-white opacity-0 hover:bg-primary-medium focus:outline-none focus-visible:bg-primary-medium motion-safe:transition",
					styles.button,
				)}
			>
				<FontAwesomeIcon icon={faAngleLeft} />
			</button>
			<button
				type="button"
				title="next slide"
				ref={nextElRef}
				className={cn(
					{
						"h-10 w-10 text-xl": size === "DEFAULT",
						"h-6 w-6": size === "small",
					},
					"-translate-y-1/2 invisible absolute top-1/2 right-4 z-9 rounded-full bg-primary text-white opacity-0 hover:bg-primary-medium focus:outline-none focus-visible:bg-primary-medium motion-safe:transition",
					styles.button,
				)}
			>
				<FontAwesomeIcon icon={faAngleRight} />
			</button>
		</>
	);
};

interface PaginationProps extends ComponentProps<"div"> {
	paginationElRef: RefObject<HTMLDivElement | null>;
}

const SliderPagination: FC<PaginationProps> = ({ paginationElRef, ...props }) => {
	return (
		<div className={cn(props.className, "mt-4 flex h-4 items-center justify-center")}>
			<div ref={paginationElRef} className={cn(styles.navigation, "swiper-pagination")} />
		</div>
	);
};
